/**
 * Système centralisé de gestion des styles et alignements pour les tableaux
 * Solution optimale et maintenable pour tous les composants de tableau
 */

import { THEME_COLORS } from '../constants/themeColors';

// Types de données et leurs configurations par défaut
export const DATA_TYPES = {
  TEXT: { alignment: 'left', width: '200px', minWidth: '120px' },
  NUMBER: { alignment: 'right', width: '80px', minWidth: '60px' },
  PERCENTAGE: { alignment: 'center', width: '70px', minWidth: '50px' },
  CURRENCY: { alignment: 'right', width: '100px', minWidth: '80px' },
  DATE: { alignment: 'center', width: '120px', minWidth: '100px' },
  STATUS: { alignment: 'center', width: '100px', minWidth: '80px' },
  ID: { alignment: 'center', width: '60px', minWidth: '40px' }
};

// Configuration des alignements selon le type de données
export const ALIGNMENT_CONFIG = Object.fromEntries(
  Object.entries(DATA_TYPES).map(([key, config]) => [key.toLowerCase(), config.alignment])
);

// Styles standardisés pour les tableaux
export const TABLE_STYLES = {
  // Styles pour les en-têtes - Couleur du thème de l'application
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: THEME_COLORS.PRIMARY, // Couleur du thème principal
    color: THEME_COLORS.WHITE, // Texte blanc pour contraste
    borderBottom: `2px solid ${THEME_COLORS.PRIMARY_DARK}`, // Version plus foncée
    padding: '12px 8px',
    fontSize: '14px'
  },
  
  // Styles pour les cellules de données
  cell: {
    padding: '8px',
    fontSize: '13px',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  
  // Styles spécifiques pour les pourcentages
  percentageCell: {
    padding: '8px',
    fontSize: '13px',
    borderBottom: '1px solid #eee',
    textAlign: 'center',
    fontWeight: '600',
    color: THEME_COLORS.PRIMARY_LIGHT, // Couleur du thème pour les pourcentages
    backgroundColor: 'transparent' // Fond transparent pour lisibilité
  },
  
  // Styles pour la ligne de total
  totalRow: {
    fontWeight: 'bold',
    backgroundColor: THEME_COLORS.PRIMARY_LIGHT,
    borderTop: `2px solid ${THEME_COLORS.PRIMARY}`
  },
  
  // Styles pour les cellules de total
  totalCell: {
    padding: '12px 8px',
    fontWeight: 'bold',
    fontSize: '14px',
    backgroundColor: THEME_COLORS.PRIMARY, // Couleur du thème pour les totaux
    color: THEME_COLORS.WHITE
  }
};

/**
 * RÈGLES D'ALIGNEMENT POUR L'APPLICATION TRAVEL MANAGER
 * 
 * Règles définies:
 * - Libellés des colonnes (headers): CENTRÉ
 * - Texte dans les cellules: GAUCHE  
 * - Pourcentages: CENTRÉ
 * - Chiffres/Montants: DROITE (pour alignement décimales)
 * - Dates: GAUCHE
 * - Ajustement automatique de la largeur des colonnes
 */

/**
 * Détecte le type de données d'une colonne
 * @param {string} columnKey - Clé de la colonne
 * @param {Array|any} data - Données pour analyse (tableau ou valeur unique)
 * @returns {string} - Clé de DATA_TYPES (ex: 'TEXT', 'NUMBER', etc.)
 */
export const detectDataType = (columnKey, data = []) => {
  const key = columnKey.toLowerCase();
  
  if (key === 'id' || key === '_id') return 'ID';
  
  if (/pourcentage|percent|%|commission|taux|ratio|marge\s*%/i.test(key)) {
    return 'PERCENTAGE';
  }
  
  if (/montant|prix|tarif|depouillement|marge|cout|total|amount|price|nbr|nombre|pax|quantity/i.test(key)) {
    return 'CURRENCY';
  }
  
  if (/date|created|updated|debut|fin|start|end|createdAt|updatedAt/i.test(key)) {
    return 'DATE';
  }

  // Analyse des valeurs
  if (Array.isArray(data) && data.length > 0) {
    const sampleValues = data.slice(0, 5).map(row => row[columnKey]);
    
    if (sampleValues.some(val => typeof val === 'number')) return 'NUMBER';
    if (sampleValues.some(val => /^\d{4}-\d{2}-\d{2}/.test(String(val)))) return 'DATE';
  }
  
  return 'TEXT';
};

/**
 * Calcule la largeur optimale d'une colonne basée sur son contenu réel
 * Analyse les valeurs pour déterminer la largeur nécessaire
 */
export const calculateColumnWidth = (columnKey, data = [], headerText) => {
  const typeKey = detectDataType(columnKey, data);
  const config = DATA_TYPES[typeKey] || DATA_TYPES.TEXT;

  // Largeur minimale basée sur le type
  const typeMinWidth = parseInt(config.minWidth) || 60;

  // Largeur basée sur le texte de l'en-tête
  const headerLabel = headerText || columnKey;
  const headerWidth = Math.max(headerLabel.length * 9 + 24, typeMinWidth);

  // Largeur basée sur le contenu des cellules (échantillon des 20 premières lignes)
  let maxContentWidth = headerWidth;
  if (Array.isArray(data) && data.length > 0) {
    const sample = data.slice(0, 20);
    for (const row of sample) {
      const val = row[columnKey];
      if (val === null || val === undefined) continue;
      const str = String(val);
      // Estimation : ~7.5px par caractère + padding
      const estimatedWidth = Math.min(str.length * 7.5 + 24, 320);
      if (estimatedWidth > maxContentWidth) maxContentWidth = estimatedWidth;
    }
  }

  // Caps par défaut plus compacts (AFF-02)
  const caps = {
    ID: 50,
    DATE: 100,
    NUMBER: 80,
    CURRENCY: 110,
    PERCENTAGE: 70,
    STATUS: 100,
    TEXT: 220,
  };
  const cap = caps[typeKey] || 220;
  const finalWidth = Math.min(Math.ceil(maxContentWidth), cap);

  return {
    width: `${finalWidth}px`,
    minWidth: `${Math.max(typeMinWidth, 60)}px`,
    alignment: config.alignment
  };
};

/**
 * RÈGLES D'ALIGNEMENT APPLIQUÉES SELON LES SPÉCIFICATIONS:
 * - Headers (libellés): CENTRE
 * - Texte: GAUCHE
 * - Pourcentages: CENTRE 
 * - Chiffres/Montants: DROITE (alignement décimal)
 * - Dates: GAUCHE
 */
export const getColumnAlignment = (columnKey, data = []) => {
  const typeKey = detectDataType(columnKey, data);
  return DATA_TYPES[typeKey]?.alignment || 'left';
};

/**
 * Alignment pour les headers - TOUJOURS CENTRÉS selon les règles
 */
export const getHeaderAlignment = () => {
  return 'center';
};

/**
 * Vérifie si une colonne est un pourcentage
 */
export const isPercentageColumn = (columnKey, value, data = []) => {
  return detectDataType(columnKey, data) === 'PERCENTAGE';
};

/**
 * Analyse une colonne pour détecter les pourcentages
 */
export const analyzeColumnForPercentage = (columnKey, data = []) => {
  return detectDataType(columnKey, data) === 'PERCENTAGE';
};

/**
 * Formate une valeur de pourcentage
 */
export const formatPercentageValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    return value <= 1 ? `${Math.round(value * 100)}%` : `${Math.round(value)}%`;
  }
  return String(value).includes('%') ? value : `${value}%`;
};

/**
 * Génère les styles complets pour une cellule de tableau
 * @param {string} columnKey - Clé de la colonne
 * @param {*} value - Valeur de la cellule
 * @param {boolean} isHeader - Si c'est un en-tête
 * @param {boolean} isTotal - Si c'est une ligne de total
 * @param {string} customType - Type personnalisé
 * @param {Array} data - Données optionnelles pour l'analyse de type
 * @returns {Object} - Objet de styles CSS
 */
export const getCellStyles = (columnKey, value, isHeader = false, isTotal = false, customType = null, data = null) => {
  // Pour les en-têtes, utiliser l'alignement centré et ignorer la détection de pourcentage
  if (isHeader) {
    return { ...TABLE_STYLES.cell, ...TABLE_STYLES.header };
  }
  
  // Pour les totaux, utiliser les styles de total et ignorer la détection de pourcentage
  if (isTotal) {
    return { ...TABLE_STYLES.cell, ...TABLE_STYLES.totalCell };
  }
  
  // Obtenir l'alignement pour les cellules de données normales
  const alignment = getColumnAlignment(columnKey, data || []);
  let baseStyles = { ...TABLE_STYLES.cell, textAlign: alignment };
  
  // Styles spéciaux pour les colonnes de pourcentage - SEULEMENT pour les cellules de données
  if (isPercentageColumn(columnKey, value, data)) {
    baseStyles = { ...baseStyles, ...TABLE_STYLES.percentageCell };
  }
  
  return baseStyles;
};

/**
 * Configuration personnalisée pour des colonnes spécifiques
 */
export const CUSTOM_COLUMN_CONFIG = {
  // Configuration pour des colonnes spécifiques de l'application
  'numero': { type: DATA_TYPES.TEXT, alignment: 'left', minWidth: '100px' },
  'nom': { type: DATA_TYPES.TEXT, alignment: 'left', minWidth: '150px' },
  'prenom': { type: DATA_TYPES.TEXT, alignment: 'left', minWidth: '120px' },
  'email': { type: DATA_TYPES.TEXT, alignment: 'left', minWidth: '180px' },
  'telephone': { type: DATA_TYPES.TEXT, alignment: 'left', minWidth: '120px' },
  'tarif': { type: DATA_TYPES.CURRENCY, alignment: 'right', minWidth: '100px' },
  'montant': { type: DATA_TYPES.CURRENCY, alignment: 'right', minWidth: '100px' },
  'marge': { type: DATA_TYPES.CURRENCY, alignment: 'right', minWidth: '100px' },
  'marge %': { type: DATA_TYPES.PERCENTAGE, alignment: 'center', minWidth: '70px' },
  'marge_pourcentage': { type: DATA_TYPES.PERCENTAGE, alignment: 'center', minWidth: '70px' },
  'pourcentage': { type: DATA_TYPES.PERCENTAGE, alignment: 'center', minWidth: '80px' },
  'taux': { type: DATA_TYPES.PERCENTAGE, alignment: 'center', minWidth: '70px' },
  'etat': { type: DATA_TYPES.STATUS, alignment: 'center', minWidth: '80px' },
  'date': { type: DATA_TYPES.DATE, alignment: 'center', minWidth: '90px' },
  'dateVol': { type: DATA_TYPES.DATE, alignment: 'center', minWidth: '90px' }
};

/**
 * Hook personnalisé pour gérer les configurations de tableau
 * @param {Array} columns - Liste des colonnes
 * @param {Array} data - Données du tableau
 * @returns {Object} - Configuration complète du tableau
 */
export const useTableConfig = (columns, data) => {
  const config = columns.map(column => {
    const key = typeof column === 'string' ? column : column.key;
    const label = typeof column === 'string' ? column : column.label;
    
    const sampleValue = data.length > 0 ? data[0][key] : null;
    const typeKey = detectDataType(key, data);
    const colConfig = calculateColumnWidth(key, data, label);
    
    return {
      key,
      label,
      dataType: typeKey,
      alignment: colConfig.alignment,
      width: colConfig.width,
      minWidth: colConfig.minWidth,
      styles: getCellStyles(key, sampleValue)
    };
  });
  
  return {
    columns: config,
    tableStyles: TABLE_STYLES
  };
};

const tableUtilsExports = {
  DATA_TYPES,
  ALIGNMENT_CONFIG,
  TABLE_STYLES,
  detectDataType,
  getColumnAlignment,
  calculateColumnWidth,
  getCellStyles,
  isPercentageColumn,
  CUSTOM_COLUMN_CONFIG,
  useTableConfig,
  formatPercentageValue,
  analyzeColumnForPercentage,
  getHeaderAlignment
};

export default tableUtilsExports;
